import type { IconName } from '../../icons';

/** The six tones of brief §5.4. `violet` is design-time (drafts, AI). */
export type StatusTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral' | 'violet';

export interface StatusEntry {
  /** English default label (sentence case). */
  label: string;
  /** i18n key the app may resolve through `StatusLabelProvider`. */
  labelKey?: string;
  tone: StatusTone;
  icon?: IconName;
  /** `dot` (default) draws the 6px leading dot; `none` for badges that carry an icon instead. */
  dotStyle?: 'dot' | 'none';
  /** Struck-through label (cancelled). */
  strike?: boolean;
}

export type StatusMap = Record<string, StatusEntry>;

/** Entity key used for values shared by every entity (`ACTIVE`, `INACTIVE`, `DRAFT` …). */
export const GENERIC_ENTITY = '*';

const registry = new Map<string, StatusMap>();

/** Normalises a backend value: trims, upper-cases, converts separators to `_`. */
export function normalizeStatusValue(value: string): string {
  // A record whose status the server left out (null / undefined at runtime, whatever the type says) reads as an
  // unknown status — it used to throw inside render and take the whole record page down (2026-09-25, live sweep).
  return String(value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
}

/** "IN_PROGRESS" → "In progress" (sentence case, brief §8.9). */
export function humanizeStatusValue(value: string): string {
  const words = normalizeStatusValue(value).toLowerCase().split('_').filter(Boolean);
  if (words.length === 0) return String(value ?? '');
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

/** i18n key of one (entity, value) pair: `status.<entity>.<VALUE>`; the generic map uses `common`. */
export function statusLabelKey(entity: string, value: string): string {
  const scope = entity.trim() === GENERIC_ENTITY ? 'common' : entity.trim();
  return `status.${scope}.${normalizeStatusValue(value)}`;
}

/**
 * Registers (or extends) the statuses of one entity type. Later registrations override earlier keys. Every
 * entry receives a `labelKey` (`status.<entity>.<VALUE>`) unless it brings its own, so the app can seed its
 * i18n bundle from `statusLabelKeys()`.
 */
export function registerStatuses(entity: string, map: StatusMap): void {
  const key = entity.trim();
  const existing = registry.get(key) ?? {};
  const next: StatusMap = { ...existing };
  for (const [value, entry] of Object.entries(map)) {
    const normalized = normalizeStatusValue(value);
    next[normalized] = { labelKey: statusLabelKey(key, normalized), ...entry };
  }
  registry.set(key, next);
}

export interface ResolvedStatus extends StatusEntry {
  /** False when neither the entity nor the generic map knows the value; the label is then humanised. */
  known: boolean;
  value: string;
}

/** Resolves a backend value for an entity, falling back to the generic map, then to a humanised neutral. */
export function resolveStatus(entity: string | undefined, value: string): ResolvedStatus {
  const key = normalizeStatusValue(value);
  const own = entity ? registry.get(entity.trim())?.[key] : undefined;
  const generic = registry.get(GENERIC_ENTITY)?.[key];
  const entry = own ?? generic;
  if (entry) return { ...entry, known: true, value: key };
  return { label: humanizeStatusValue(value), tone: 'neutral', known: false, value: key };
}

export function listStatuses(entity: string): StatusMap {
  return { ...(registry.get(entity.trim()) ?? {}) };
}

export function listStatusEntities(): string[] {
  return Array.from(registry.keys());
}

/** Every `{ labelKey, label }` pair in the registry, for seeding the app's i18n bundle. */
export function statusLabelKeys(): { labelKey: string; label: string; entity: string; value: string }[] {
  const out: { labelKey: string; label: string; entity: string; value: string }[] = [];
  for (const [entity, map] of registry) {
    for (const [value, entry] of Object.entries(map)) {
      out.push({ labelKey: entry.labelKey ?? statusLabelKey(entity, value), label: entry.label, entity, value });
    }
  }
  return out;
}

/** Deep copy of the whole registry (`entity → value → entry`), for tests and tooling. */
export function statusRegistrySnapshot(): Record<string, StatusMap> {
  const out: Record<string, StatusMap> = {};
  for (const [entity, map] of registry) {
    out[entity] = Object.fromEntries(Object.entries(map).map(([value, entry]) => [value, { ...entry }]));
  }
  return out;
}

/** Test helper: clears every registration. */
export function resetStatusRegistry(): void {
  registry.clear();
}

/** Baseline generic values from brief §5.4; `statuses/generic.ts` widens the code-level fallback, entity maps live in `statuses/*.ts`. */
registerStatuses(GENERIC_ENTITY, {
  DRAFT: { label: 'Draft', tone: 'violet' },
  PENDING: { label: 'Pending', tone: 'warning' },
  ACTIVE: { label: 'Active', tone: 'success' },
  INACTIVE: { label: 'Inactive', tone: 'neutral' },
  OPEN: { label: 'Open', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  BLOCKED: { label: 'Blocked', tone: 'danger' },
  DONE: { label: 'Done', tone: 'success' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  OVERDUE: { label: 'Overdue', tone: 'danger' },
  QUEUED: { label: 'Queued', tone: 'neutral' },
  RUNNING: { label: 'Running', tone: 'info' },
  FAILED: { label: 'Failed', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', strike: true },
  EXCEPTION: { label: 'Exception', tone: 'danger' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  IN_TRANSIT: { label: 'In transit', tone: 'info' },
  IN_REVIEW: { label: 'In review', tone: 'warning' },
  PUBLISHED: { label: 'Published', tone: 'success' },
  DEPRECATED: { label: 'Deprecated', tone: 'neutral' },
  MAINTENANCE: { label: 'Maintenance', tone: 'warning' },
  PARTIAL: { label: 'Partial', tone: 'warning' },
});
